import { AlertTriangle, CreditCard } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBilling } from '../context/BillingContext';

const daysUntil = (value) => value
  ? Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86400000))
  : null;

export default function BillingBanner({ inline = false }) {
  const { subscription } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  if (!subscription) return null;

  const trialDays = daysUntil(subscription.trial_end_date);
  const status = subscription.billing_status;
  const isBillingPage = location.pathname === '/settings/billing';
  const goToBilling = () => navigate('/settings/billing');

  if (status === 'canceled' && !isBillingPage) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 px-4" data-testid="billing-canceled-overlay">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-7 text-center shadow-2xl">
          <CreditCard className="mx-auto h-7 w-7 text-slate-500" />
          <h2 className="mt-4 text-xl font-black text-[#0D1F3C]">Subscription ended</h2>
          <p className="mt-2 text-sm text-slate-600">Contact our team to restore access to your BidSignalIQ deployment.</p>
          <button onClick={() => navigate('/request-quote')} className="mt-5 rounded-md bg-[#0D7C8F] px-5 py-2.5 text-sm font-bold text-white">Request a quote</button>
        </div>
      </div>
    );
  }

  if (status === 'past_due') {
    return (
      <button onClick={goToBilling} className={`${inline ? 'relative w-full shrink-0' : 'fixed left-0 right-0 top-16 md:left-[220px]'} z-40 flex min-h-10 items-center justify-center gap-2 bg-red-50 px-4 py-2 text-sm font-bold text-red-800`} data-testid="billing-past-due-banner">
        <AlertTriangle className="h-4 w-4" /> Payment failed. Update your payment method to avoid losing access. <span className="underline">Fix now</span>
      </button>
    );
  }

  if (status === 'trialing' && trialDays !== null) {
    const submitted = subscription.quote_request_status === 'submitted';
    return (
      <div className={`${inline ? 'relative w-full shrink-0' : 'fixed left-0 right-0 top-16 md:left-[220px]'} z-40 flex min-h-11 items-center justify-center bg-amber-50 px-4 py-2 text-center text-sm font-bold text-amber-900`} data-testid="billing-trial-banner">
        <button onClick={() => navigate('/request-quote')} className="underline underline-offset-2">
          {`You're on a 7-day free trial — ${trialDays} day${trialDays === 1 ? '' : 's'} remaining.`}
          {submitted ? ' Our team is preparing your deployment quote.' : ' Request your quote to continue after the trial.'}
        </button>
      </div>
    );
  }

  return null;
}
